import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  LayoutDashboard, 
  Server, 
  Monitor, 
  Smartphone, 
  Network, 
  Settings, 
  Globe, 
  Container, 
  Activity, 
  Bug, 
  FileText, 
  BarChart3, 
  Users, 
  Database, 
  Search, 
  Shield, 
  Brain, 
  Zap, 
  TrendingUp, 
  Bell, 
  Plug, 
  Cloud, 
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Menu,
  LogOut,
  Sparkles,
  Cpu,
  Eye,
  Mail,
  Package,
  Sliders,
  Clock,
  Plus,
  Rocket,
  CheckCircle2,
  BookOpen,
  Ticket,
  ListTodo,
  UserCheck,
  Bot,
  Workflow,
  Gauge,
  PieChart,
  AlertTriangle,
  MessageSquare
} from 'lucide-react';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import logoImage from '../../assets/coremonit-logo.svg';
import { SidebarProfileHub } from './SidebarProfileHub';
import { isTeamChatEnabled } from '../../modules/team-chat/featureFlag';
import { microservicesNav, isMicroservicesModuleEnabled } from '../../modules/microservices/mount';
import { isIntegrationsModuleEnabled } from '../../modules/integrations/featureFlag';
import { useIntegrations } from '../../modules/integrations/context/IntegrationsContext';
import {
  buildIntegrationsModuleSection,
  type IntegrationMenuItem,
} from '../../modules/integrations/integrationsSidebar';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline';
  children?: MenuItem[];
  glowColor?: string;
}

interface DashboardSidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileMenuOpen?: boolean;
  onToggleMobileMenu?: () => void;
}

const LEGACY_MICROSERVICES_CHILDREN: MenuItem[] = [
  { id: 'microservices/overview', label: 'Microservices Overview', icon: Eye, badge: '24', glowColor: 'cyber-cyan' },
  { id: 'microservices/add', label: 'Add Microservice', icon: Plus, glowColor: 'cyber-green' },
  { id: 'microservices/service-mesh', label: 'Service Mesh', icon: Network, badge: 'AI', badgeVariant: 'default', glowColor: 'cyber-blue' },
  { id: 'microservices/distributed-tracing', label: 'Distributed Tracing', icon: TrendingUp, glowColor: 'cyber-purple' },
  { id: 'microservices/agent-scripts', label: 'Agent Scripts', icon: FileText, badge: '12', glowColor: 'cyber-cyan' },
  { id: 'microservices/apis', label: 'APIs', icon: Globe, glowColor: 'cyber-cyan' },
  { id: 'microservices/websites', label: 'Websites', icon: Globe, glowColor: 'cyber-blue' },
];

function buildMicroservicesModuleChildren(): MenuItem[] {
  return microservicesNav.map((item) => ({
    id: item.path === '/' ? 'microservices' : `microservices${item.path}`,
    label: item.label,
    icon: item.icon as React.ComponentType<{ className?: string }>,
    badge: item.badge,
    badgeVariant: item.badgeColor === 'purple' ? 'default' as const : 'secondary' as const,
    glowColor: 'cyber-cyan',
  }));
}

function toMenuItem(item: IntegrationMenuItem): MenuItem {
  return {
    id: item.id,
    label: item.label,
    icon: item.icon,
    badge: item.badge,
    badgeVariant: item.badgeVariant,
    glowColor: item.glowColor,
    children: item.children?.map(toMenuItem),
  };
}

function menuContainsPage(item: MenuItem, pageId: string): boolean {
  if (item.id === pageId) return true;
  return item.children?.some((child) => menuContainsPage(child, pageId)) ?? false;
}

function findAncestorSectionIds(items: MenuItem[], pageId: string, path: string[] = []): string[] | null {
  for (const item of items) {
    if (item.id === pageId) return path;
    if (item.children?.length) {
      const found = findAncestorSectionIds(item.children, pageId, [...path, item.id]);
      if (found !== null) return found;
    }
  }
  return null;
}

function resolveMenuItems(connectedIntegrations: ReturnType<typeof useIntegrations>['integrations'] = []): MenuItem[] {
  const moduleChildren = isMicroservicesModuleEnabled() ? buildMicroservicesModuleChildren() : [];

  const resolved = menuItems.map((item) => {
    if (item.id === 'microservices') {
      return {
        ...item,
        children: [...LEGACY_MICROSERVICES_CHILDREN, ...moduleChildren],
      };
    }
    if (item.id === 'system' && isIntegrationsModuleEnabled()) {
      return {
        ...item,
        children: (item.children ?? []).filter((child) => child.id !== 'system/integrations'),
      };
    }
    return item;
  });

  if (!isIntegrationsModuleEnabled()) {
    return resolved;
  }

  const integrationsSection = toMenuItem(buildIntegrationsModuleSection(connectedIntegrations));
  const systemIndex = resolved.findIndex((item) => item.id === 'system');
  const insertAt = systemIndex >= 0 ? systemIndex : resolved.length;
  return [...resolved.slice(0, insertAt), integrationsSection, ...resolved.slice(insertAt)];
}

const menuItems: MenuItem[] = [
  {
    id: 'onboarding',
    label: 'Onboarding',
    icon: Rocket,
    badge: 'START',
    badgeVariant: 'default',
    glowColor: 'cyber-cyan',
    children: [
      { id: 'onboarding/overview', label: 'Onboarding Overview', icon: Rocket, glowColor: 'cyber-cyan' },
      { id: 'onboarding/add-project', label: 'Add Project', icon: Plus, glowColor: 'cyber-green' },
      { id: 'onboarding/add-infrastructure', label: 'Add Infrastructure', icon: Server, glowColor: 'cyber-blue' },
      { id: 'onboarding/add-monitoring-sources', label: 'Add Monitoring Sources', icon: Activity, glowColor: 'cyber-purple' },
      { id: 'onboarding/validation-status', label: 'Validation Status', icon: CheckCircle2, glowColor: 'cyber-green' },
      { id: 'onboarding/setup-guide', label: 'Setup Guide', icon: BookOpen, glowColor: 'cyber-cyan' },
    ]
  },
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    badge: 'AI',
    badgeVariant: 'default',
    glowColor: 'cyber-cyan',
    children: [
      { id: 'overview/super-dashboard', label: 'Super Dashboard', icon: LayoutDashboard, glowColor: 'cyber-cyan' },
      { id: 'overview/dashboard', label: 'Dashboard', icon: Activity, glowColor: 'cyber-blue' },
      { id: 'overview/dashboard-builder', label: 'Dashboard Builder', icon: Settings, glowColor: 'cyber-purple' }
    ]
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    icon: Activity,
    glowColor: 'cyber-green',
    children: [
      { id: 'monitoring/app-performance', label: 'Application Performance', icon: TrendingUp, glowColor: 'cyber-green' },
      { id: 'monitoring/errors', label: 'Error Tracking', icon: Bug, badge: '3', badgeVariant: 'destructive', glowColor: 'cyber-magenta' },
      { id: 'monitoring/logs', label: 'Logs & Analytics', icon: FileText, glowColor: 'cyber-cyan' },
      { id: 'monitoring/user-sessions', label: 'User Sessions', icon: Users, badge: '1.2k', glowColor: 'cyber-blue' },
      { id: 'monitoring/alerts', label: 'Alerts & Notifications', icon: Bell, badge: '7', badgeVariant: 'destructive', glowColor: 'cyber-magenta' }
    ]
  },
  {
    id: 'server-mgmt',
    label: 'Server Management',
    icon: Server,
    glowColor: 'cyber-blue',
    children: [
      { id: 'server-mgmt/servers', label: 'Server Dashboard', icon: Server, badge: '12', glowColor: 'cyber-blue' },
      { id: 'server-mgmt/networks', label: 'Network Monitoring', icon: Network, glowColor: 'cyber-blue' },
      { id: 'server-mgmt/databases', label: 'Database Performance', icon: Database, badge: '5', glowColor: 'cyber-purple' },
      { id: 'server-mgmt/containers', label: 'Container Orchestration', icon: Container, badge: '48', glowColor: 'cyber-blue' },
      { id: 'server-mgmt/services', label: 'Service Health', icon: Settings, badge: '24', glowColor: 'cyber-purple' },
      { id: 'server-mgmt/patch-management', label: 'Patch Management', icon: Package, badge: '15', badgeVariant: 'destructive', glowColor: 'cyber-magenta' },
      { id: 'server-mgmt/fine-tuning', label: 'Fine-Tuning', icon: Sliders, badge: 'AI', badgeVariant: 'default', glowColor: 'cyber-cyan' },
      { id: 'server-mgmt/cron-scripts', label: 'Cron & Scripts', icon: Clock, badge: '32', glowColor: 'cyber-green' }
    ]
  },
  {
    id: 'ai',
    label: 'AI Intelligence',
    icon: Brain,
    badge: 'PRO',
    badgeVariant: 'default',
    glowColor: 'cyber-purple',
    children: [
      { id: 'ai/assistant', label: 'AI Assistant', icon: Sparkles, badge: 'NEW', badgeVariant: 'default', glowColor: 'cyber-purple' },
      { id: 'ai/insights', label: 'AI Insights', icon: Eye, badge: '12', glowColor: 'cyber-green' },
      { id: 'ai/self-healing', label: 'Self-Healing', icon: Zap, badge: 'SAFE', badgeVariant: 'default', glowColor: 'cyber-cyan' },
      { id: 'ai/spike-analysis', label: 'Spike Analysis (RCA)', icon: TrendingUp, glowColor: 'cyber-magenta' },
      { id: 'ai/predictive-analytics', label: 'Predictive Analytics', icon: BarChart3, glowColor: 'cyber-blue' },
      { id: 'ai/smart-alerts', label: 'AI Smart Alerts', icon: Bell, badge: '3', glowColor: 'cyber-cyan' }
    ]
  },
  {
    id: 'microservices',
    label: 'Microservices',
    icon: Globe,
    glowColor: 'cyber-cyan',
    children: LEGACY_MICROSERVICES_CHILDREN,
  },
  {
    id: 'ticketing',
    label: 'Ticketing',
    icon: Ticket,
    badge: 'NEW',
    badgeVariant: 'default',
    glowColor: 'cyber-purple',
    children: [
      { id: 'ticketing/dashboard', label: 'Ticket Dashboard', icon: LayoutDashboard, badge: '23', glowColor: 'cyber-cyan' },
      { id: 'ticketing/create', label: 'Create Ticket', icon: Plus, glowColor: 'cyber-green' },
      { id: 'ticketing/my-tickets', label: 'My Tickets', icon: UserCheck, badge: '5', glowColor: 'cyber-blue' },
      { id: 'ticketing/team-tickets', label: 'Team Tickets', icon: Users, badge: '18', glowColor: 'cyber-purple' },
      { id: 'ticketing/ai-generated', label: 'AI Generated Tickets', icon: Bot, badge: 'AI', badgeVariant: 'default', glowColor: 'cyber-purple' },
      { id: 'ticketing/automation', label: 'Ticket Automation Rules', icon: Workflow, glowColor: 'cyber-cyan' },
      { id: 'ticketing/integrations', label: 'Integrations', icon: Plug, badge: '6', glowColor: 'cyber-blue' },
      { id: 'ticketing/sla', label: 'SLA Management', icon: Gauge, badge: '2', badgeVariant: 'destructive', glowColor: 'cyber-magenta' },
      { id: 'ticketing/analytics', label: 'Ticket Analytics', icon: PieChart, glowColor: 'cyber-green' }
    ]
  },
  {
    id: 'incidents',
    label: 'Incident Management',
    icon: AlertTriangle,
    badge: '3',
    badgeVariant: 'destructive',
    glowColor: 'cyber-magenta',
    children: [
      { id: 'incidents', label: 'All Incidents', icon: ListTodo, badge: '12', glowColor: 'cyber-magenta' }
    ]
  },
  {
    id: 'incident-commander',
    label: 'AI Incident Commander',
    icon: AlertTriangle,
    badge: 'AI',
    badgeVariant: 'default',
    glowColor: 'cyber-magenta',
    children: [
      { id: 'incident-commander/dashboard', label: 'Incident Dashboard', icon: AlertTriangle, badge: '3', badgeVariant: 'destructive', glowColor: 'cyber-magenta' },
      { id: 'incident-commander/war-room/wr-001', label: 'War Rooms', icon: MessageSquare, badge: '2', glowColor: 'cyber-cyan' },
      { id: 'incident-commander/predictions', label: 'Incident Predictions', icon: Brain, badge: 'AI', badgeVariant: 'default', glowColor: 'cyber-purple' },
      { id: 'incident-commander/knowledge-graph', label: 'Knowledge Graph', icon: Network, glowColor: 'cyber-blue' },
      { id: 'incident-commander/engineers', label: 'Engineer Assignment', icon: Users, badge: '8', glowColor: 'cyber-green' }
    ]
  },
  {
    id: 'time-machine',
    label: 'Observability',
    icon: Clock,
    badge: 'NEW',
    badgeVariant: 'default',
    glowColor: 'cyber-cyan',
    children: [
      { id: 'time-machine/timeline-explorer', label: 'Timeline Explorer', icon: Clock, badge: 'HOT', badgeVariant: 'default', glowColor: 'cyber-cyan' },
      { id: 'time-machine/infrastructure-replay', label: 'Infrastructure Replay', icon: Activity, glowColor: 'cyber-blue' },
      { id: 'time-machine/incident-playback', label: 'Incident Playback', icon: AlertTriangle, glowColor: 'cyber-magenta' },
      { id: 'time-machine/system-snapshots', label: 'System Snapshots', icon: Eye, badge: '5', glowColor: 'cyber-green' },
      { id: 'time-machine/state-diff-analyzer', label: 'State Diff Analyzer', icon: BarChart3, glowColor: 'cyber-purple' }
    ]
  },
  {
    id: 'team',
    label: 'Team',
    icon: Users,
    glowColor: 'cyber-blue',
    children: [
      { id: 'team/members', label: 'Team Members', icon: Users, badge: '8', glowColor: 'cyber-blue' },
      { id: 'team/chat', label: 'Team Chat', icon: MessageSquare, badge: 'NEW', badgeVariant: 'default', glowColor: 'cyber-cyan' },
      { id: 'roles-permissions', label: 'Roles & Permissions', icon: Shield, glowColor: 'cyber-green' },
      { id: 'collaboration', label: 'Collaboration Tools', icon: Users, glowColor: 'cyber-cyan' }
    ]
  },
  {
    id: 'achievements',
    label: 'Achievements',
    icon: Sparkles,
    badge: 'NEW',
    badgeVariant: 'default',
    glowColor: 'cyber-green',
    children: [
      { id: 'achievements/badges', label: 'Badges & Rewards', icon: Sparkles, glowColor: 'cyber-green' },
      { id: 'leaderboard', label: 'Team Leaderboard', icon: TrendingUp, glowColor: 'cyber-purple' },
      { id: 'challenges', label: 'Weekly Challenges', icon: Zap, glowColor: 'cyber-cyan' }
    ]
  },
  {
    id: 'system',
    label: 'System',
    icon: Settings,
    glowColor: 'cyber-cyan',
    children: [
      { id: 'system/agents', label: 'Agents', icon: Server, glowColor: 'cyber-cyan' },
      { id: 'system/settings', label: 'System Settings', icon: Settings, glowColor: 'cyber-cyan' },
      { id: 'system/integrations', label: 'Integrations', icon: Plug, badge: '12', glowColor: 'cyber-cyan' },
      { id: 'system/cloud-providers', label: 'Cloud Providers', icon: Cloud, badge: '8', glowColor: 'cyber-blue' },
      { id: 'system/security-events', label: 'Security & Compliance', icon: Shield, badge: '2', badgeVariant: 'destructive', glowColor: 'cyber-magenta' }
    ]
  },
  {
    id: 'payment',
    label: 'Payment',
    icon: BarChart3,
    glowColor: 'cyber-green',
    children: [
      { id: 'billing', label: 'Billing & Usage', icon: BarChart3, glowColor: 'cyber-green' },
      { id: 'subscription', label: 'Subscription Plans', icon: Zap, glowColor: 'cyber-cyan' },
      { id: 'invoices', label: 'Invoices & Receipts', icon: FileText, glowColor: 'cyber-blue' }
    ]
  },
  {
    id: 'support',
    label: 'Support',
    icon: HelpCircle,
    glowColor: 'cyber-green',
    children: [
      { id: 'help-center', label: 'Help Center', icon: HelpCircle, glowColor: 'cyber-green' },
      { id: 'contact-support', label: 'Contact Support', icon: Mail, glowColor: 'cyber-cyan' },
      { id: 'documentation', label: 'Documentation', icon: FileText, glowColor: 'cyber-blue' }
    ]
  },
];

export function DashboardSidebar({
  currentPage,
  onNavigate,
  isCollapsed,
  onToggleCollapse,
  isMobileMenuOpen = false,
  onToggleMobileMenu
}: DashboardSidebarProps) {
  // Initialize expanded sections from localStorage or default to overview
  const [expandedSections, setExpandedSections] = useState<string[]>(() => {
    const saved = localStorage.getItem('expandedSections');
    return saved ? JSON.parse(saved) : ['onboarding'];
  });
  const { user, logout } = useAuth();
  const { integrations: connectedIntegrations } = useIntegrations();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [desktopWidth, setDesktopWidth] = useState(() => {
    const saved = localStorage.getItem('sidebarWidth');
    const parsed = Number(saved);
    if (Number.isFinite(parsed)) {
      return Math.min(420, Math.max(240, parsed));
    }
    return 320;
  });
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const resizeStartRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const resolvedMenuItems = resolveMenuItems(connectedIntegrations);

  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Save expanded sections to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('expandedSections', JSON.stringify(expandedSections));
  }, [expandedSections]);

  useEffect(() => {
    localStorage.setItem('sidebarWidth', String(desktopWidth));
  }, [desktopWidth]);

  // Auto-expand parent sections when a child route is active (supports nested menus)
  useEffect(() => {
    const ancestors = findAncestorSectionIds(resolvedMenuItems, currentPage);
    if (!ancestors?.length) return;
    setExpandedSections((prev) => {
      const merged = [...prev];
      let changed = false;
      for (const id of ancestors) {
        if (!merged.includes(id)) {
          merged.push(id);
          changed = true;
        }
      }
      return changed ? merged : prev;
    });
  }, [currentPage, resolvedMenuItems]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleItemClick = (item: MenuItem) => {
    if (isCollapsed && !(isMobile || isTablet) && item.children && item.children.length > 0) {
      onNavigate(item.children[0].id);
      return;
    }
    if (item.children) {
      toggleSection(item.id);
    } else {
      onNavigate(item.id);
      // Close mobile menu when navigating on mobile
      if (isMobile && onToggleMobileMenu) {
        onToggleMobileMenu();
      }
    }
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const isActive = currentPage === item.id;
    const isExpanded = expandedSections.includes(item.id);
    const hasChildren = item.children && item.children.length > 0;
    const isParentActive = hasChildren && menuContainsPage(item, currentPage) && !isActive;

    return (
      <div key={item.id} className="w-full">
        <motion.button
          onClick={() => handleItemClick(item)}
          className={`w-full flex items-center ${
            isCollapsed && !(isMobile || isTablet) ? 'justify-center px-2 py-2.5' : 'gap-3 px-4 py-3'
          } rounded-xl text-left transition-all duration-300 group relative overflow-hidden ${
            isActive || isParentActive
              ? 'bg-gradient-to-r from-cyber-cyan/16 via-cyber-blue/10 to-transparent text-cyber-blue dark:from-card-bg dark:to-card-bg/50 dark:text-cyber-cyan border border-cyber-cyan/35 dark:border-card-border shadow-lg shadow-cyber-cyan/20'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/80 dark:hover:bg-card-bg/30 hover:border-slate-300/60 dark:hover:border-card-border/50 border border-transparent'
          } ${level === 1 ? 'ml-4 pl-8 py-2' : ''} ${level === 2 ? 'ml-6 pl-10 py-1.5' : ''} ${level >= 3 ? 'ml-8 pl-12 py-1' : ''}`}
          whileHover={{ scale: 1.02, x: level === 0 ? 4 : 2 }}
          whileTap={{ scale: 0.98 }}
          style={{
            backdropFilter: isActive || isParentActive ? 'blur(20px)' : 'none'
          }}
        >
          {/* Active glow effect */}
          {(isActive || isParentActive) && (
            <>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-glow-cyan/10 via-glow-blue/5 to-transparent rounded-xl"
                layoutId="activeGlow"
                transition={{ duration: 0.4 }}
              />
              <motion.div
                className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-cyber-cyan via-cyber-blue to-cyber-purple rounded-r-full shadow-lg shadow-cyber-cyan/50"
                layoutId="activeIndicator"
                transition={{ duration: 0.4 }}
              />
            </>
          )}

          {/* Hover glow effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-glow-cyan/5 to-glow-blue/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          />

          {/* Icon with glow */}
          <div className="relative z-10">
            <item.icon className={`w-5 h-5 flex-shrink-0 transition-all duration-300 ${
              isActive || isParentActive ? 'text-cyber-cyan drop-shadow-[0_0_8px_rgba(0,209,255,0.6)]' : 
              'text-slate-500 group-hover:text-cyber-cyan group-hover:drop-shadow-[0_0_6px_rgba(0,209,255,0.4)]'
            }`} />
          </div>

          {/* Label */}
          {!isCollapsed && (
            <>
              <span className={`flex-1 font-medium truncate transition-all duration-300 ${
                isActive || isParentActive ? 'text-slate-900 dark:text-white' : 'group-hover:text-slate-900 dark:group-hover:text-white'
              }`}>
                {item.label}
              </span>

              {/* Badge */}
              {item.badge && (
                <Badge 
                  variant={item.badgeVariant || 'secondary'} 
                  className={`text-xs px-2 py-1 font-medium transition-all duration-300 ${
                    item.badgeVariant === 'destructive' 
                      ? 'bg-gradient-to-r from-red-500/20 to-red-600/20 text-red-400 border border-red-500/30 shadow-md shadow-red-500/20' :
                    item.badgeVariant === 'default' 
                      ? 'bg-gradient-to-r from-cyber-cyan/20 to-cyber-blue/20 text-cyber-cyan border border-cyber-cyan/30 shadow-md shadow-cyber-cyan/20' :
                    'bg-gradient-to-r from-slate-600/20 to-slate-700/20 text-slate-300 border border-slate-600/30'
                  }`}
                >
                  {item.badge}
                </Badge>
              )}

              {/* Expand/Collapse icon */}
              {hasChildren && (
                <motion.div
                  animate={{ rotate: isExpanded ? 90 : 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="relative z-10"
                >
                  <ChevronRight className={`w-4 h-4 transition-colors duration-300 ${
                    isActive || isParentActive ? 'text-cyber-cyan' : 'text-slate-500 group-hover:text-cyber-cyan'
                  }`} />
                </motion.div>
              )}
            </>
          )}
        </motion.button>

        {/* Children */}
        <AnimatePresence>
          {hasChildren && isExpanded && !isCollapsed && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="mt-2 space-y-2 relative">
                {/* Connection line */}
                <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-card-border via-card-border/50 to-transparent" />
                {item.children
                  .filter((child) => child.id !== 'team/chat' || isTeamChatEnabled())
                  .map((child) => renderMenuItem(child, level + 1))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const handleSidebarResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isMobile || isTablet || isCollapsed) return;
    event.preventDefault();
    event.stopPropagation();
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    setIsResizingSidebar(true);
    resizeStartRef.current = { startX: event.clientX, startWidth: desktopWidth };
  };

  useEffect(() => {
    if (!isResizingSidebar) return;

    const onPointerMove = (event: PointerEvent) => {
      if (!resizeStartRef.current) return;
      document.body.style.cursor = 'ew-resize';
      const deltaX = event.clientX - resizeStartRef.current.startX;
      const next = Math.min(420, Math.max(240, resizeStartRef.current.startWidth + deltaX));
      setDesktopWidth(next);
    };

    const onPointerUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setIsResizingSidebar(false);
      resizeStartRef.current = null;
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp, { once: true });
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [isResizingSidebar]);

  return (
    <>
      {/* Mobile/Tablet Overlay */}
      {(isMobile || isTablet) && isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onToggleMobileMenu}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
        />
      )}

      {/* Sidebar */}
      <motion.aside
        data-scroll-zone="sidebar"
        className={`${
          isMobile || isTablet
            ? `fixed inset-y-0 left-0 z-50 w-[min(20rem,92vw)] transform ${
                isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
              } transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0`
            : ''
        } h-full min-h-0 max-h-full bg-card/95 dark:bg-gradient-to-b dark:from-dashboard-bg dark:to-dashboard-bg/95 backdrop-blur-xl border-r border-border flex flex-col relative shadow-2xl flex-shrink-0 overflow-hidden`}
        animate={!(isMobile || isTablet) ? { width: isCollapsed ? 64 : desktopWidth } : {}}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        style={{
          width: !(isMobile || isTablet) ? (isCollapsed ? 64 : desktopWidth) : undefined,
          boxShadow: '4px 0 24px rgba(0, 209, 255, 0.1)'
        }}
      >
        {/* Animated border */}
        <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-cyber-cyan/30 to-transparent dark:via-cyber-cyan/30" />
        
        {/* Sidebar Header */}
        <div className={`${isCollapsed && !(isMobile || isTablet) ? 'p-3' : 'p-6'} border-b border-border relative`}>
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div className="flex items-center space-x-3" transition={{ duration: 0.2 }}>
              <div className="relative">
                <img 
                  src={logoImage} 
                  alt="CoreMonit Logo" 
                  className="w-10 h-10 rounded-xl object-contain relative z-10"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-cyber-cyan/40 via-cyber-blue/40 to-cyber-purple/40 rounded-xl blur-lg opacity-75 animate-pulse" />
                <div className="absolute inset-0 bg-gradient-to-br from-cyber-cyan/20 to-cyber-blue/20 rounded-xl" />
              </div>
              {(!isCollapsed || isMobile || isTablet) && (
                <div>
                  <span className="text-xl font-bold bg-gradient-to-r from-cyber-cyan via-cyber-blue to-cyber-purple bg-clip-text text-transparent">
                    CoreMonit
                  </span>
                  <p className="text-xs text-muted-foreground mt-0.5">AI-Powered Monitoring</p>
                </div>
              )}
            </motion.div>

            {/* Collapse Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={isMobile || isTablet ? onToggleMobileMenu : onToggleCollapse}
              className="p-2 hover:bg-muted text-muted-foreground hover:text-cyber-cyan transition-all duration-300 border border-transparent hover:border-border rounded-lg backdrop-blur-sm"
            >
              <Menu className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Collapsed: compact profile + presence hub */}
        {isCollapsed && !(isMobile || isTablet) && (
          <div className="border-b border-border px-2 py-2">
            <SidebarProfileHub user={user} variant="compact" />
          </div>
        )}

        {/* User profile — full card opens presence, DMs, alerts */}
        {(!isCollapsed || isMobile || isTablet) && (
          <motion.div
            className="border-b border-border p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <SidebarProfileHub user={user} variant="card" />
          </motion.div>
        )}

        {/* Navigation Menu */}
        <div
          className={`cm-sidebar-scroll-zone flex-1 min-h-0 overflow-y-auto overscroll-y-contain ${isCollapsed && !(isMobile || isTablet) ? 'py-3 px-2' : 'py-6 px-4'} space-y-2 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-card-border/50 touch-pan-y`}
        >
          <nav className="space-y-2">
            {resolvedMenuItems.map(item => renderMenuItem(item))}
          </nav>
        </div>

        {/* Sidebar Footer */}
        <div className={`mt-auto sticky bottom-0 border-t border-card-border/50 bg-card/95 backdrop-blur-xl ${isCollapsed && !(isMobile || isTablet) ? 'p-2' : 'p-4'}`}>
          {(!isCollapsed || isMobile || isTablet) ? (
            <Button
              variant="ghost"
              onClick={logout}
              className="w-full justify-start text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/80 dark:hover:bg-card-bg/50 transition-all duration-300 border border-transparent hover:border-slate-300/70 dark:hover:border-card-border/50 rounded-lg backdrop-blur-sm group"
            >
              <LogOut className="w-4 h-4 mr-3 transition-colors duration-300 group-hover:text-cyber-cyan" />
              Sign Out
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="w-full p-3 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/80 dark:hover:bg-card-bg/50 transition-all duration-300 border border-transparent hover:border-slate-300/70 dark:hover:border-card-border/50 rounded-lg backdrop-blur-sm group"
            >
              <LogOut className="w-4 h-4 transition-colors duration-300 group-hover:text-cyber-cyan" />
            </Button>
          )}
        </div>

        {/* Ambient glow effect */}
        <div className="absolute top-1/4 -right-32 w-64 h-64 bg-gradient-to-l from-cyber-cyan/5 to-transparent rounded-full blur-3xl pointer-events-none" />
        {!isMobile && !isTablet && !isCollapsed && (
          <div
            onPointerDown={handleSidebarResizeStart}
            className="absolute top-0 right-0 h-full w-1.5 cursor-ew-resize bg-transparent hover:bg-cyber-cyan/20 transition-colors"
            title="Drag to resize sidebar"
            aria-label="Drag to resize sidebar"
          />
        )}
      </motion.aside>
    </>
  );
}